import { isMidiNumber, type LiveEvent, type TimedLiveEvent } from "./live-events";
import type { LiveFramePayload } from "./live-transport";

/**
 * Sérialisation binaire de ce qui circule entre participants.
 *
 * # Pourquoi pas du JSON
 *
 * Le budget n'est pas la taille, il est le temps. Un paquet en mode `lossy`
 * doit tenir sous la MTU (~1300 octets) sinon il est fragmenté, et un fragment
 * perdu emporte tout le paquet. Quatre octets par geste laissent tenir une
 * salve de plusieurs dizaines de notes dans un seul datagramme.
 *
 * # Le format
 *
 * ```
 *   octet 0      version du protocole
 *   octet 1      type de trame
 *   octets 2..5  horodatage : l'horloge du service en ms, modulo 2^32 (u32 BE) ;
 *                zéro quand l'émetteur ne la connaît pas encore
 *   puis, selon le type :
 *
 *   GESTES
 *     octet 6    nombre d'événements (1..MAX_EVENTS_PER_FRAME)
 *     puis 8 octets par événement :
 *       +0  écart en ms depuis l'horodatage de trame (0..255) — l'instant
 *           du geste, sur la même horloge
 *       +1  nature (1 = noteOn, 2 = noteOff, 3 = sustain)
 *       +2  donnée A — note MIDI, ou 0/1 pour la pédale
 *       +3  donnée B — vélocité 0..127, 0 sinon
 *       +4  tick du transport partagé (u32 BE)
 *
 *   ÉTAT
 *     octet 6    nombre d'événements (1..MAX_EVENTS_PER_FRAME)
 *     puis 4 octets par événement : les quatre premiers ci-dessus
 *
 *   BATTEMENT
 *     rien : l'en-tête seul
 * ```
 *
 * # Les trois types, et pourquoi ils existent
 *
 * **Gestes** : ce que quelqu'un vient de jouer. C'est l'essentiel du trafic.
 *
 * **État** : la liste complète de ce qu'un participant tient *en ce moment*.
 * Envoyée périodiquement, elle est le filet sous le mode non fiable — un
 * note-off perdu laisserait sinon une touche baissée indéfiniment. Elle est
 * idempotente et insensible à l'ordre, ce qu'un renvoi de note-off ne serait
 * pas : réémettre un relâchement peut couper une note rejouée entre-temps,
 * réaffirmer un état ne le peut pas. Elle règle aussi, gratuitement, le cas de
 * celui qui rejoint au milieu d'un accord.
 *
 * **Battement** : rien du tout, six octets, à cadence fixe et sur demande
 * seulement. Il ne porte pas de musique ; il tient les radios éveillées — une
 * carte Wi-Fi qui n'a rien à envoyer se met en veille et ne reçoit plus qu'au
 * rythme des balises de son point d'accès, par salves de cent millisecondes et
 * plus. Voir `lib/live/radio-keepalive.ts` pour la mesure qui l'a validé et
 * sa cadence. Il n'a pas fait changer la version du format : un onglet
 * qui ne le connaît pas l'ignore, ce qui est exactement ce qu'il doit en faire.
 *
 * # Ce qui n'y est plus, et pourquoi rien d'autre n'y entrera
 *
 * Le canal de données ne porte que des **gestes** : ce qu'un participant vient
 * de jouer, et ce qu'il tient. Tout ce sur quoi deux participants pourraient
 * être en désaccord — la position, la pause, le tempo, le délai de
 * restitution, ce que chaque machine coûte — est un fait de séance : il
 * appartient au service de temps, qui le publie dans la métadonnée de salle
 * (voir `session-state.ts`). La règle de décision tient en une phrase : si
 * deux participants peuvent en être en désaccord, ça passe par le service ;
 * sinon, par ici.
 *
 * Deux trames l'ont appris à leurs dépens :
 *
 * - la trame « pair » (type 6) — jambe jusqu'au SFU, latence audio, pause et
 *   position chez l'émetteur, diffusée toutes les deux secondes — est partie
 *   avec la version 10. La jambe s'annonce au service, qui en déduit le délai
 *   de restitution de toute la salle ; la pause et la position y étaient déjà,
 *   de façon autoritaire, là où l'annonce d'un pair n'était qu'un ouï-dire
 *   pouvant arriver dans le désordre ;
 * - la trame « transport » (type 5) — la séance se fige ou repart, annoncé de
 *   pair à pair — est partie avec la version 11. Elle doublait la métadonnée,
 *   et le double coûtait : sur une vraie pause, le service publiait, chaque
 *   client appliquait, et chaque client **rediffusait** — le verrou de
 *   réentrance du pont ne couvrait que les trames reçues, pas l'application
 *   venue de la métadonnée. Chaque récepteur adoptait alors la position du
 *   dernier émetteur au lieu de celle du service. Deux autorités pour une même
 *   chose ; il n'en reste qu'une.
 *
 * Ni le type 5 ni le type 6 ne sont réattribués.
 *
 * # Le numéro de version
 *
 * Il permet de refuser proprement une trame qu'on ne sait pas lire, au lieu de
 * la décoder de travers. Deux participants peuvent avoir des versions
 * différentes de l'app ouvertes en même temps — l'un a rechargé la page,
 * l'autre non. Un onglet resté en version 9 ou 10 continue d'envoyer ses
 * trames « pair » ou « transport » : elles sont ignorées ici, pas lues de
 * travers — et surtout pas appliquées.
 */

export const LIVE_FRAME_VERSION = 11;

/** Version + type + horodatage. */
const HEADER_BYTES = 6;

/** Octets par événement dans une trame d'état. */
const EVENT_BYTES = 4;

/**
 * Octets par événement dans une trame de gestes : les quatre de l'état, plus le
 * tick du transport partagé.
 *
 * Le tick est écrit en absolu et non en écart à une base de trame. Un écart
 * tiendrait sur deux octets et en économiserait deux — mais il faudrait alors
 * décider ce que vaut un écart négatif, ce qu'un déplacement de tête de lecture
 * pendant une salve produit. Quatre octets valent mieux que cette question :
 * une trame pleine pèse 48 × 8 + 7 = 391 octets, toujours très loin de la MTU.
 */
const GESTURE_EVENT_BYTES = 8;

/**
 * Plafond d'événements par trame.
 *
 * 391 octets au pire, très en deçà de la MTU. Le plafond n'est pas là pour la
 * taille mais pour borner ce qu'une trame malformée peut faire allouer.
 */
export const MAX_EVENTS_PER_FRAME = 48;

/** Écart maximal encodable dans une trame ; au-delà, il faut une trame neuve. */
export const MAX_FRAME_SPAN_MS = 255;

const TYPE_GESTURES = 1;
const TYPE_STATE = 2;
const TYPE_KEEPALIVE = 3;
// 5 : la trame « transport », retirée en version 11. Ne pas réattribuer.
// 6 : la trame « pair », retirée en version 10. Ne pas réattribuer.

const KIND_NOTE_ON = 1;
const KIND_NOTE_OFF = 2;
const KIND_SUSTAIN = 3;

export type LiveFrame =
  | { type: "gestures"; sentAt: number; events: readonly TimedLiveEvent[] }
  | { type: "state"; sentAt: number; events: readonly LiveEvent[] }
  | { type: "keepalive"; sentAt: number };

// ── Encodage ─────────────────────────────────────────────────

/**
 * Encode une salve de gestes. Les événements doivent tenir dans
 * `MAX_FRAME_SPAN_MS` après `sentAt` et ne pas dépasser `MAX_EVENTS_PER_FRAME`
 * — c'est au regroupeur (`LiveSession`) de découper, pas à l'encodeur de
 * deviner.
 */
export function encodeGestures(sentAt: number, events: readonly TimedLiveEvent[]): LiveFramePayload {
  assertCount(events.length);

  const bytes = allocate(TYPE_GESTURES, sentAt, 1 + events.length * GESTURE_EVENT_BYTES);
  bytes[HEADER_BYTES] = events.length;

  const view = new DataView(bytes.buffer);

  events.forEach((timed, index) => {
    const offset = HEADER_BYTES + 1 + index * GESTURE_EVENT_BYTES;
    bytes[offset] = clampByte(Math.round(timed.at - sentAt), MAX_FRAME_SPAN_MS);
    writeEvent(bytes, offset + 1, timed.event);
    view.setUint32(offset + 4, Math.max(0, Math.round(timed.tick)) >>> 0);
  });

  return bytes;
}

/** Encode l'état complet d'un participant : ce qu'il tient à cet instant. */
export function encodeState(sentAt: number, events: readonly LiveEvent[]): LiveFramePayload {
  assertCount(events.length);

  const bytes = allocate(TYPE_STATE, sentAt, 1 + events.length * EVENT_BYTES);
  bytes[HEADER_BYTES] = events.length;

  events.forEach((event, index) => {
    const offset = HEADER_BYTES + 1 + index * EVENT_BYTES;
    // L'écart n'a pas de sens pour un état : il décrit un instant, pas une
    // suite. On écrit zéro plutôt que de laisser un octet indéterminé.
    bytes[offset] = 0;
    writeEvent(bytes, offset + 1, event);
  });

  return bytes;
}

/** Encode un battement : l'en-tête, et rien d'autre. */
export function encodeKeepalive(sentAt: number): LiveFramePayload {
  return allocate(TYPE_KEEPALIVE, sentAt, 0);
}

// ── Décodage ─────────────────────────────────────────────────

/**
 * Décode une trame reçue. Rend `null` sur tout ce qui n'est pas lisible :
 * mauvaise version, type inconnu, longueur incohérente.
 *
 * Le silence est ici le bon comportement. Ces octets viennent du réseau, donc
 * d'une version de l'app qu'on ne contrôle pas, éventuellement plus récente que
 * la nôtre. Lever ferait tomber la session entière sur une trame qu'il suffisait
 * d'ignorer.
 */
export function decodeFrame(bytes: Uint8Array): LiveFrame | null {
  if (bytes.length < HEADER_BYTES || bytes[0] !== LIVE_FRAME_VERSION) {
    return null;
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const type = bytes[1];
  const sentAt = view.getUint32(2);

  if (type === TYPE_KEEPALIVE) {
    // Un battement qui porterait quelque chose n'en est pas un.
    if (bytes.length !== HEADER_BYTES) {
      return null;
    }

    return { type: "keepalive", sentAt };
  }

  if (type !== TYPE_GESTURES && type !== TYPE_STATE) {
    return null;
  }

  if (bytes.length < HEADER_BYTES + 1) {
    return null;
  }

  const count = bytes[HEADER_BYTES];

  if (count === 0 || count > MAX_EVENTS_PER_FRAME) {
    return null;
  }

  const eventBytes = type === TYPE_STATE ? EVENT_BYTES : GESTURE_EVENT_BYTES;

  if (bytes.length !== HEADER_BYTES + 1 + count * eventBytes) {
    return null;
  }

  const timed: TimedLiveEvent[] = [];

  for (let index = 0; index < count; index += 1) {
    const offset = HEADER_BYTES + 1 + index * eventBytes;
    const event = readEvent(bytes, offset + 1);

    if (event === null) {
      return null;
    }

    timed.push({
      event,
      at: sentAt + bytes[offset],
      tick: type === TYPE_STATE ? 0 : view.getUint32(offset + 4),
    });
  }

  if (type === TYPE_STATE) {
    return { type: "state", sentAt, events: timed.map((entry) => entry.event) };
  }

  return { type: "gestures", sentAt, events: timed };
}

// ── Détail d'encodage ────────────────────────────────────────

function allocate(type: number, sentAt: number, bodyBytes: number): LiveFramePayload {
  const bytes = new Uint8Array(HEADER_BYTES + bodyBytes);
  bytes[0] = LIVE_FRAME_VERSION;
  bytes[1] = type;
  new DataView(bytes.buffer).setUint32(2, sentAt >>> 0);

  return bytes;
}

function assertCount(count: number): void {
  if (count === 0 || count > MAX_EVENTS_PER_FRAME) {
    throw new RangeError(`Trame de ${count} événements hors bornes (1..${MAX_EVENTS_PER_FRAME}).`);
  }
}

function clampByte(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

function writeEvent(bytes: Uint8Array, offset: number, event: LiveEvent): void {
  if (event.kind === "noteOn") {
    bytes[offset] = KIND_NOTE_ON;
    bytes[offset + 1] = event.midi;
    bytes[offset + 2] = clampByte(Math.round(event.velocity * 127), 127);

    return;
  }

  if (event.kind === "noteOff") {
    bytes[offset] = KIND_NOTE_OFF;
    bytes[offset + 1] = event.midi;
    bytes[offset + 2] = 0;

    return;
  }

  bytes[offset] = KIND_SUSTAIN;
  bytes[offset + 1] = event.down ? 1 : 0;
  bytes[offset + 2] = 0;
}

function readEvent(bytes: Uint8Array, offset: number): LiveEvent | null {
  const kind = bytes[offset];
  const a = bytes[offset + 1];
  const b = bytes[offset + 2];

  if (kind === KIND_NOTE_ON) {
    if (!isMidiNumber(a)) {
      return null;
    }

    return { kind: "noteOn", midi: a, velocity: Math.min(127, b) / 127 };
  }

  if (kind === KIND_NOTE_OFF) {
    if (!isMidiNumber(a)) {
      return null;
    }

    return { kind: "noteOff", midi: a };
  }

  if (kind === KIND_SUSTAIN) {
    return { kind: "sustain", down: a === 1 };
  }

  return null;
}
